<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Clients') }}
    </template>
  </PageHeader>
  <GraphqlDataTable
    :query="query"
    :order-by="initialOrder"
    :column-black-list="['id']"
    :filter-black-list="['accounts']"
    :search-placeholder="$t('Search clients…')"
    :bulk-actions="bulkActions"
  >
    <template #action="{ row, index }">
      <ClientActionColumn :row="row" :row-index="index" @open-page="openClientPage" />
    </template>
  </GraphqlDataTable>
  <Teleport :to="drawerTarget">
    <ClientDrawer
      :show="showDrawer"
      :client-relation-id="drawerClientId ?? ''"
      @update:show="onDrawerToggle"
      @open-page="openClientPage"
    />
  </Teleport>
  <PageOverlayRegion
    :open="pageClientId !== null"
    :label="$t('Client page')"
    data-test="client-page-overlay"
    @close="closeClientPage"
  >
    <ClientPage
      v-if="pageClientId"
      :client-relation-id="pageClientId"
      overlay
      @close="closeClientPage"
    />
  </PageOverlayRegion>
  <LinkRecruitersModal
    v-if="bulkLinkClients.length > 0"
    :show="showBulkLinkModal"
    mode="bulk"
    :clients="bulkLinkClients"
    @update:show="onBulkLinkToggle"
  />
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsIconNameEnum } from '@worksome/wds-components'
import { useOverlayPage } from '@/composables/useOverlayPage'
import PageOverlayRegion from '@/components/PageOverlayRegion.vue'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import PageHeader from '@/components/PageHeader.vue'
import ClientDrawer from '@/components/Clients/ClientDrawer.vue'
import { CLIENT_ID_KEY, useClientDrawer } from '@/components/Clients/useClientDrawer'
import ClientPage from '@/components/Clients/ClientPage.vue'
import ClientActionColumn from '@/components/GraphqlDataTable/columns/CompanySupplier/ClientActionColumn.vue'
import LinkRecruitersModal from '@/components/Clients/LinkRecruitersModal.vue'
import { graphql } from '@/gql'
import { SortOrder, CompanyClientOrderByColumn } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import GlobalEncoder from '@/utils/Encoders/GlobalEncoder'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import type { PeopleSegment } from '@/components/Clients/clientPageTabs'
import { peopleQuery } from '@/components/Clients/People/peopleLinks'

const query = graphql(`
  query SupplierClientsTableQuery(
    $accounts: [ID!]
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [CompanyClientOrderByClauseInput!]
    $clientRegions: MarketRegionInput
    $linkedStaffingAgencies: [ID!]
    $createdAtDateRange: DateRangeInput
    $paymentTermsFollowsClient: Boolean
    $hasLinkedStaffingAgencies: Boolean
    $clientOwners: [ID!]
  ) {
    companyClients(
      accounts: $accounts
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      clientRegions: $clientRegions
      linkedStaffingAgencies: $linkedStaffingAgencies
      createdAtDateRange: $createdAtDateRange
      paymentTermsFollowsClient: $paymentTermsFollowsClient
      hasLinkedStaffingAgencies: $hasLinkedStaffingAgencies
      owners: $clientOwners
    ) {
      data {
        id
        ...ClientNameColumn
        ...ClientRecruitersColumn
        ...ClientOwnersColumn
        ...ClientPlacementsColumn
        ...ClientLocationColumn
        ...ClientAddedColumn
        ...ClientActionColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const initialOrder = {
  field: CompanyClientOrderByColumn.ClientName,
  order: SortOrder.Asc,
}

const { drawerTarget, drawerClientId, showDrawer, onDrawerToggle } = useClientDrawer()

function clientPageUrl(companySupplierId: string) {
  return laravelNamedRoute(namedRoutes.frontend.clients.view, {
    companySupplier: companySupplierId,
  })
}

const {
  id: pageClientId,
  open: openPage,
  close: closeClientPage,
} = useOverlayPage({
  backLink: laravelNamedRoute(namedRoutes.frontend.clients.index, {}),
  viewUrl: clientPageUrl,
  idFromPath: (path) => {
    const [, id] = path.match(/\/clients\/([^/?#]+)/) ?? []

    return id ? decodeURIComponent(id) : null
  },
})

function openClientPage(companySupplierId: string, segment?: PeopleSegment) {
  onDrawerToggle(false)
  // Straight to the encoder: the debounced push would land after the pathname changed and leave ?client-id= behind.
  GlobalEncoder.push({ key: CLIENT_ID_KEY, query: null })
  GlobalEncoder.forceUpdateHistory()
  openPage(companySupplierId, segment ? peopleQuery(segment) : undefined)
}

type SelectedClientRow = { company?: { id: string; name: string } }

const showBulkLinkModal = ref(false)
const bulkLinkClients = ref<Array<{ id: string; name: string }>>([])

const bulkActions: DataTableBulkAction[] = [
  {
    name: $t('Link staffing agencies'),
    icon: WdsIconNameEnum.ADD,
    dataTest: 'bulk-link-staffing-agency',
    action: (rows) => {
      bulkLinkClients.value = Object.values(rows as Record<string, SelectedClientRow>).flatMap(
        (row) => (row.company ? [{ id: row.company.id, name: row.company.name }] : []),
      )
      showBulkLinkModal.value = bulkLinkClients.value.length > 0
    },
  },
]

function onBulkLinkToggle(show: boolean) {
  showBulkLinkModal.value = show
  if (!show) {
    bulkLinkClients.value = []
  }
}
</script>
